import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, Button, Input, Field, FieldLabel } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import toast from 'react-hot-toast';

interface NewsArticleOption {
  slug: string;
  title: string;
  excerpt: string;
}

export default function CampaignEditorPage() {
  const navigate = useNavigate();
  const [newsArticles, setNewsArticles] = useState<NewsArticleOption[]>([]);
  const [form, setForm] = useState({
    newsSlug: '',
    subject: '',
    includeUsers: true,
    includeProspects: true,
  });

  useEffect(() => {
    adminBackofficeApi.getCampaignNewsArticles().then((res) => {
      if (res.success) setNewsArticles(res.data || []);
    });
  }, []);

  const audienceConfig = {
    includeUsers: form.includeUsers,
    includeProspects: form.includeProspects,
  };

  const handleCreate = async () => {
    try {
      const res = await adminBackofficeApi.createCampaign({
        newsSlug: form.newsSlug,
        subject: form.subject,
        audienceConfig,
        status: 'draft',
      });
      if (res.success && res.data?.id) {
        toast.success('Campaign created');
        navigate(`/backoffice/campaigns/${res.data.id}`);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed');
    }
  };

  const previewRecipients = async () => {
    toast('Create the campaign first, then preview recipients on the detail page');
  };

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <PageHeader
        title="New marketing campaign"
        help={
          <p>
            Sends via Sinch Mailjet to the marketing contact list. Recipients are synced from
            opted-in users and prospects before send.
          </p>
        }
      />
      <Card>
        <CardHeader>
          <p className="text-sm text-muted-foreground">
            Sends via Sinch Mailjet to the marketing contact list. Recipients are synced from
            opted-in users and prospects before send.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field>
            <FieldLabel>News article (markdown)</FieldLabel>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={form.newsSlug}
              onChange={(e) => setForm({ ...form, newsSlug: e.target.value })}
            >
              <option value="">Select article</option>
              {newsArticles.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.title}
                </option>
              ))}
            </select>
          </Field>
          <Field>
            <FieldLabel>Email subject</FieldLabel>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          </Field>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.includeUsers}
                onChange={(e) => setForm({ ...form, includeUsers: e.target.checked })}
              />
              Sync registered users (marketing opt-in) to Mailjet before send
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.includeProspects}
                onChange={(e) => setForm({ ...form, includeProspects: e.target.checked })}
              />
              Sync consented prospects to Mailjet before send
            </label>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={!form.newsSlug || !form.subject}>
              Create campaign
            </Button>
            <Button variant="outline" onClick={previewRecipients}>
              Preview recipients
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
